/*
 * Approved copy constants for the Governance Tile · Operator column.
 *
 * Every string is word-for-word from mockup v8.8.12 lines 1117-1174 and
 * carries [COPY-APPROVED: governance-tile-v8] (2026-07-24).
 *
 * Static mockup denominators (`of 48`) and mockup placeholder integers
 * (25, 3, 9, 9, 1, 1, 1, 1, 3, 2, 3, 3, 1, 8, 1) are stripped from this
 * manifest — every row is resolved live in OperatorColumn.jsx from the
 * summary endpoint, or renders an em dash with a `-pending` testid
 * marker when the endpoint field does not exist yet (attestation-
 * honesty rule · anchor GB-013 · § V.6).
 *
 * The legend banner in the mockup is a static explanatory prose block
 * with no numeric content; it ships verbatim.
 */

export const OP_SCORECARD_TITLE = 'How the Operator is going';
export const OP_SCORECARD_ICON = '\uD83D\uDC64'; // 👤
export const OP_SUBTITLE =
  '(Admin) · every number here counts a SYSTEM failure you caught · goal: trends to 0 as E1 + Judge improve';

// Status chip fragments composed live in OperatorColumn.jsx from
// (tasks_total, got_wrong).  Never composed as a single mockup string
// so no stale "48" or stale "25 catches" can leak into the DOM.
export const OP_STATUS_ZERO = 'System quiet';
export const OP_STATUS_LOAD_PREFIX = 'System-load'; // followed by ' · N catches'

// Headline sub-line halves (yellow accent then slate prose).
export const OP_HEADLINE_SUB_ACCENT = 'catches you had to make';
export const OP_HEADLINE_SUB_REST =
  ' · every number on this tile counts a SYSTEM failure caught by you · not a failure by you · goal: trends to 0';
export const OP_HEADLINE_SUB_ZERO = 'No operator catches logged in this session yet.';

// Legend banner (yellow-tinted background · v8 addition).
export const OP_LEGEND_STRONG = 'Read this tile as system-load, not operator-fault.';
export const OP_LEGEND_BODY =
  'Amber numbers below count times the system (E1 + the Judge) failed to spot something before you did. You catching them is you doing your job well. Numbers trend down as E1 + Judge improve. The traffic light is red because the SYSTEM state is red, not because you are at fault.';

// Section 1: sub-topic breakdown of operator catches.  All row-level
// values are unmeasured today (pending IJ-034 / IJ-045); the section
// total binds to `got_wrong` from the endpoint.
export const OP_CATCHES_LABEL = 'Times you caught what E1 and the Judge got wrong';
export const OP_CATCHES_ROWS = [
  {
    key: 'governance-breaches',
    label: 'Governance breaches you surfaced',
    tooltip:
      'Entries logged in GOVERNANCE_LEDGER.md that would not have been caught without your intervention.',
  },
  {
    key: 'tile-misses',
    label: 'Tile misses you eyeballed (Phase 1 shipped tile)',
  },
  {
    key: 'mockup-misses',
    label: 'Mockup misses you eyeballed (v5 → v8 review)',
  },
  {
    key: 'reply-guard-misses',
    label: 'Reply-Guard misses you called out',
  },
  {
    key: 'copy-attestation-misses',
    label: 'Copy-approval attestation misses you flagged',
  },
  {
    key: 'keyword-lookup-misses',
    label: 'Keyword-lookup misses you called out',
  },
  {
    key: 'interpretation-rewrites',
    label: 'Interpretation rewrites you had to make',
    tooltip: 'Times you had to reword an interpretation for accuracy (credit vs truth).',
  },
];
export const OP_CATCHES_TOTAL_LABEL =
  'Section total (system failures caught by you this session)';

// Section 2: your inputs to me this session.  The section label carries
// an explanatory tooltip clarifying that this is a count-not-a-score.
export const OP_INPUTS_LABEL = 'Your inputs to me this session';
export const OP_INPUTS_TOOLTIP =
  'This section is a count of your inputs (green-lights, red-flags, backlog directives, copy approvals) — not a scored performance section. No traffic light applied here. Row colours are visual grouping only (green = go, red = stop, amber = defer).';
export const OP_INPUTS_ROWS = [
  {
    key: 'green-lights',
    label: 'Green-lights given',
    tone: 'green',
  },
  {
    key: 'red-flags',
    label: 'Red-flags raised (stop / do not ship / revert)',
    tone: 'red',
  },
  {
    key: 'backlog-directives',
    label: 'Items you told me to backlog',
    tone: 'amber',
  },
  {
    key: 'copy-approved',
    label: 'Copy strings you approved (in-thread [COPY-APPROVED: ...])',
    tone: 'green',
  },
  {
    key: 'copy-revoked',
    label: 'Copy strings you revoked / backlogged',
    tone: 'red',
  },
];

// Section 3: signals we cannot auto-detect yet.
export const OP_SIGNALS_LABEL = 'Signals we cannot auto-detect yet';
export const OP_SIGNALS_ROWS = [
  {
    key: 'lessons-cited',
    label: 'Past lessons you cited (counter not wired)',
    tooltip:
      'Every time you referenced a prior rule, agreement, or precedent to catch me. Wiring plan: NLP-tag your messages for citations to § K, § S, § T, GB-###, or IJ-### references.',
  },
  {
    key: 'ground-truth-flips',
    label: 'Judge misses caught after the fact',
    tooltip:
      'Every Judge miss you had to correct with the Wrong/Right ground-truth toggle. Per LESSON-013, each of these is also an E1 miss (E1 wrong first, Judge missed). Renamed from "Ground-truth flips" (LESSON-024 · counter-name truthfulness · 2026-07-25).',
  },
];
